import { useContext, useEffect, useReducer } from 'react';
import * as React from 'react';
import useVersion from '../../helpers/useVersion';
import { useSetAtom } from 'jotai';
import  userTokenAtom  from '../atoms/userTokenAtom';
import { useUser } from 'reactfire';

/* *** STATE DECLARATION */

interface IAppState {
  appWidth: number;
  isSidebarExpanded: boolean;
  sidebarTitle: string;
  isSearchModalOpen: boolean;
  version: string;
  isNewVersion: boolean;
}
/*  *** ACTIONS  */
export enum appStateActions {
  SETAPPWIDTH,
  EXPANDSIDEBAR,
  CONTRACTSIDEBAR,
  SETSIDEBARTITLE,
  OPENSEARCHMODAL,
  CLOSESEARCHMODAL,
  SETVERSION,
  SETISNEWVERSION,
}

/**** INITIAL STATE */

const initialState: IAppState = {
  isSearchModalOpen: false,
  appWidth: 0,
  isSidebarExpanded: false,
  sidebarTitle: '',
  version: '',
  isNewVersion: false,
};

let _lastIdleTimeUpdated = new Date();

export const getIdleTime = () => {
  return Math.floor(
    (new Date().getTime() - _lastIdleTimeUpdated.getTime()) / 1000,
  );
};

const resetIdleTime = () => {
  _lastIdleTimeUpdated = new Date();
};

/*  *** REDUCER */

const reducer = (state: IAppState, action: Action) => {
  const actionHandlers = {
    [appStateActions.SETAPPWIDTH]: () => ({
      ...state,
      appWidth: action.payload,
    }),
    [appStateActions.EXPANDSIDEBAR]: () => ({
      ...state,
      isSidebarExpanded: true,
    }),
    [appStateActions.CONTRACTSIDEBAR]: () => ({
      ...state,
      isSidebarExpanded: false,
    }),
    [appStateActions.SETSIDEBARTITLE]: () => ({
      ...state,
      sidebarTitle: action.payload,
    }),
    [appStateActions.OPENSEARCHMODAL]: () => ({
      ...state,
      isSearchModalOpen: true,
    }),
    [appStateActions.CLOSESEARCHMODAL]: () => ({
      ...state,
      isSearchModalOpen: false,
    }),
    [appStateActions.SETVERSION]: () => ({
      ...state,
      version: action.payload,
    }),
    [appStateActions.SETISNEWVERSION]: () => ({
      ...state,
      isNewVersion: action.payload,
    }),

  };

  const actionHandler = actionHandlers[action.type];

  if (actionHandler) {
    return actionHandler();
  }

  throw new Error(`Unhandled action type: ${action.type}`);
};

//////////////////////////////////////////////////////////////////////////////////////////////
type Action = {
  type: appStateActions;
  payload?: any;
};

interface IAppContext {
  state: IAppState;
  dispatch: React.Dispatch<Action>;
}

const StoreContext = React.createContext<IAppContext>({
  state: initialState,
  dispatch: () => null,
});

export const AppStateProvider = (props: any) => {

  const user = useUser();
  const setUserToken = useSetAtom(userTokenAtom);

  const [state, dispatch] = useReducer(reducer, initialState);

  const { hasChanged, data } = useVersion('/version.txt');

  useEffect(() => {
    if (hasChanged) {
      dispatch({ type: appStateActions.SETVERSION, payload: data });
      dispatch({ type: appStateActions.SETISNEWVERSION, payload: true });
    }
    dispatch({ type: appStateActions.SETVERSION, payload: data });
  }, [hasChanged, data]);

  useEffect(() => {
    document.body.addEventListener('mouseover', resetIdleTime);

    document.body.addEventListener('click', resetIdleTime);
    return () => {
      document.body.removeEventListener('mouseover', resetIdleTime);
      document.body.removeEventListener('click', resetIdleTime);
    };
  }, []);

  useEffect(() => {

    const fetchToken = async () => {
      if (!user || !user.data) return;
      const _token = await user.data.getIdToken();

      setUserToken(_token);
    };

    fetchToken();
  }, [setUserToken, user]);
  return (
    <StoreContext.Provider value={{ state, dispatch }}>
      {props.children}
    </StoreContext.Provider>
  );
};

export const useAppState = () => useContext(StoreContext);
